import React, { useEffect } from 'react'
import * as inflected from 'inflected'
import { FieldArray, Field, formValueSelector, change } from 'redux-form'
import { useSelector, useDispatch } from 'react-redux'
import { dataTypes } from '@easybutton/constants'

import { titleize } from '../../../../../utils/strings'
import Accordion from '../../../../Shared/Accordion'
import DynamicInput from '../../../../Shared/Forms/DynamicInput'
import Button from '../../../../Shared/Button'
import { IconButton } from '../../../../Shared/Icon'
import { FORM_NAME } from './Form'

import styles from './ExpandableField.module.css'

export const FieldRenderer = function FieldRenderer({ column, ...props }) {
  switch (column.dataType) {
    default:
      return (
        <DynamicInput
          {...props}
          label={column.displayName || column.name}
          placeholder="Empty"
        />
      )
  }
}

export default function ExpandableField({
  field,
  label,
  column,
  actionId,
  stepId,
  databaseId,
  table,
}) {
  const array = column.dataType === dataTypes.ARRAY

  label = inflected.titleize(inflected.humanize(inflected.underscore(label)))

  return (
    <Accordion
      carret
      indented
      className={styles.wrapper}
      title={<h3>{label}</h3>}
    >
      {() => {
        if (array) {
          return (
            <ArrayWrapper
              field={field}
              actionId={actionId}
              stepId={stepId}
              databaseId={databaseId}
              table={table}
              column={column}
            />
          )
        }

        return (
          <ObjectChild
            field={field}
            actionId={actionId}
            stepId={stepId}
            databaseId={databaseId}
            table={table}
            column={column}
          />
        )
      }}
    </Accordion>
  )
}

const ArrayItem = function ArrayItem({
  field,
  index,
  actionId,
  stepId,
  databaseId,
  table,
  column,
  onRemove,
}) {
  const prototype = column.children && column.children[0]

  let children = null

  switch (prototype && prototype.dataType) {
    case dataTypes.OBJECT:
      children = (prototype.children || []).map(child => (
        <Field
          key={child.name}
          component={FieldRenderer}
          name={`${field}.${child.name}`}
          field={`${field}.${child.name}`}
          actionId={actionId}
          stepId={stepId}
          databaseId={databaseId}
          table={table}
          column={child}
        />
      ))

      break

    default:
      children = (
        <Field
          name={field}
          component={DynamicInput}
          label={column.displayName || column.name}
          placeholder="Enter value..."
          actionId={actionId}
          stepId={stepId}
          databaseId={databaseId}
          table={table}
          column={column}
        />
      )
  }

  const name = titleize(prototype.displayName || prototype.name)
  const title = `${name} ${index + 1}`

  return (
    <div className={styles.arrayItem}>
      <div className={styles.itemTitle}>
        <h3>{title}</h3>
        <IconButton icon="delete" onClick={onRemove} color="grey" />
      </div>
      <div>{children}</div>
    </div>
  )
}

const ArrayFieldArray = function ArrayFieldArray({
  fields,
  actionId,
  stepId,
  databaseId,
  table,
  column,
}) {
  return (
    <div>
      {fields.map((itm, i) => (
        <ArrayItem
          key={itm}
          field={itm}
          actionId={actionId}
          stepId={stepId}
          databaseId={databaseId}
          table={table}
          column={column}
          index={i}
          onRemove={() => fields.remove(i)}
        />
      ))}
      <Button
        color="grey"
        icon="add"
        onClick={() => fields.push(null)}
        type="button"
      >
        Add Item
      </Button>
    </div>
  )
}

const ArrayWrapper = function ArrayWrapper({
  field,
  actionId,
  stepId,
  databaseId,
  table,
  column,
}) {
  const arrayField = `${field}.__array`

  const dispatch = useDispatch()

  const selector = formValueSelector(FORM_NAME)
  const value = useSelector(state => selector(state, arrayField))

  useEffect(() => {
    if (!value) {
      dispatch(change(FORM_NAME, field, { __array: [] }))
    }
  }, [dispatch, value])

  return (
    <FieldArray
      name={arrayField}
      component={ArrayFieldArray}
      actionId={actionId}
      stepId={stepId}
      databaseId={databaseId}
      table={table}
      column={column}
    />
  )
}

const ObjectChild = function ObjectChild({
  field,
  actionId,
  stepId,
  databaseId,
  table,
  column,
}) {
  const { children = [] } = column

  return (
    <div>
      {children.map(child => (
        <Field
          name={`${field}.${child.name}`}
          component={FieldRenderer}
          actionId={actionId}
          stepId={stepId}
          databaseId={databaseId}
          table={table}
          column={column}
        />
      ))}
    </div>
  )
}
